import React, { useState, useEffect, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { port } from '../../App'
import { HrmStore } from '../../Context/HrmContext'
import { ArrowLeft } from 'lucide-react'
import ShortcutCard from '../../Components/HomeComponent/ShortcutCard'
import BackButton from '../../Components/MiniComponent/BackButton'

const CandidateFinancialDetail = () => {
    const { id: joiningId } = useParams()
    const navigate = useNavigate()
    const { changeDateYear } = useContext(HrmStore)
    
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(null)
    const [cycles, setCycles] = useState([])

    const fetchFinancialData = async () => {
        try {
            setLoading(true)
            const res = await axios.get(`${port}/root/cms/billing-cycles/?joining_id=${joiningId}`)
            setCycles(res.data)
            if (res.data.length > 0) {
                setData(res.data[0])
            }
        } catch (error) {
            console.error("Error fetching financial details", error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchFinancialData()
    }, [joiningId])

    if (loading) return (
        <div className="flex flex-col items-center justify-center h-[70vh] poppins">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-500 fw-bold uppercase tracking-widest text-[10px]">Loading Ledger...</p>
        </div>
    )

    if (!data) return (
        <div className="p-8 text-center bg-white rounded shadow-sm border border-gray-100 poppins">
            <p className="text-gray-400 italic">No financial data found for this placement.</p>
            <button onClick={() => navigate(-1)} className="mt-4 text-blue-600 fw-bold text-sm underline">Go Back</button>
        </div>
    )

    const totalBilled = cycles.reduce((acc, c) => acc + parseFloat(c.billing_amount), 0)
    const totalPayout = cycles.reduce((acc, c) => acc + parseFloat(c.candidate_payout_amount || 0), 0)
    const totalProfit = totalBilled - totalPayout
    const profitMargin = totalBilled > 0 ? ((totalProfit / totalBilled) * 100).toFixed(1) : 0

    const clientPaid = cycles.reduce((acc, c) => acc + parseFloat(c.paid_amount || 0), 0)
    const candidatePaid = cycles.reduce((acc, c) => acc + parseFloat(c.candidate_paid_amount || 0), 0)

    return (
        <div className="p-3 poppins" style={{ backgroundColor: 'rgb(241,242,246)', minHeight: '100vh' }}>
            {/* Nav Header */}
            <div className="flex items-center gap-3 mb-4">
                <BackButton/>
                <div>
                    <h5 className="fw-bold text-gray-800 m-0">Candidate Financial Ledger</h5>
                    <p className="text-[10px] text-gray-500 fw-bold tracking-widest">Profitability & Payout Tracking</p>
                </div>
            </div>

            {/* Profile Overview Card (mimicking app style) */}
            <div className="border-2 bg-white rounded p-4 mb-4 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                    {/* <div className="w-16 h-16 bg-blue-600 rounded flex items-center justify-center text-white shadow-sm">
                        <span className="text-2xl fw-bold">{data.candidate_name?.[0]}</span>
                    </div> */}
                    <div>
                        <h4 className="fw-semibold text-gray-900 mb-1">{data.candidate_name}</h4>
                        <p className="text-blue-600 text-[12px] fw-bold m-0 tracking-tight">{data.requirement_title}</p>
                        <p className="text-gray-400 text-[11px] m-0">{data.client_name || 'Individual Client'}</p>
                    </div>
                </div>
                <div className="flex gap-4 border-l pl-4 border-gray-100 h-full items-center">
                    <div className="text-center px-4 border-r border-gray-100">
                        <p className="text-[10px] text-gray-400 fw-bold uppercase mb-1">Contract Salary</p>
                        <p className="text-lg fw-bold text-gray-800 m-0">₹{parseFloat(data.candidate_salary_per_month || 0).toLocaleString()}</p>
                    </div>
                    <div className="text-center px-4">
                        <p className="text-[10px] text-gray-400 fw-bold uppercase mb-1">Profit Yield</p>
                        <span className="bg-green-500 text-white px-3 py-1 rounded-full text-[11px] fw-bold uppercase shadow-sm">
                            {profitMargin}% Margin
                        </span>
                    </div>
                </div>
            </div>

            {/* Summary Row using ShortcutCard components */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <ShortcutCard 
                    img={'/assets/Images/circle1.png'}
                    count={`₹${totalBilled.toLocaleString()}`} 
                    label='Total Billed (In)' 
                />
                <ShortcutCard 
                    img={'/assets/Images/circle3.png'}
                    count={`₹${totalPayout.toLocaleString()}`} 
                    label='Expected Payout' 
                />
                <ShortcutCard 
                    img={'/assets/Images/circle2.png'}
                    count={`₹${totalProfit.toLocaleString()}`} 
                    label='Net Profit' 
                />
                <ShortcutCard 
                    img={'/assets/Images/circle4.png'}
                    count={`${cycles.length}`} 
                    label='Cycles logged' 
                />
            </div>

            {/* Collection & Payout Progress */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <div className="bg-white border-2 border-white rounded p-4 shadow-sm">
                    <h6 className="text-[11px] fw-bold text-blue-600 uppercase tracking-widest mb-3 border-b pb-2">Client Collection Status</h6>
                    <div className="flex justify-between items-end mb-2">
                        <span className="text-2xl fw-bold text-gray-800">₹{clientPaid.toLocaleString()}</span>
                        <span className="text-[11px] text-gray-400 fw-bold pb-1 underline decoration-blue-200">Goal: ₹{totalBilled.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-slate-100 h-2 rounded overflow-hidden">
                        <div 
                            className="bg-blue-600 h-full transition-all duration-1000" 
                            style={{ width: `${totalBilled > 0 ? (clientPaid / totalBilled) * 100 : 0}%` }}
                        ></div>
                    </div>
                </div>
                <div className="bg-white border-2 border-white rounded p-4 shadow-sm">
                    <h6 className="text-[11px] fw-bold text-orange-600 uppercase tracking-widest mb-3 border-b pb-2">Candidate Payout Status</h6>
                    <div className="flex justify-between items-end mb-2">
                        <span className="text-2xl fw-bold text-gray-800">₹{candidatePaid.toLocaleString()}</span>
                        <span className="text-[11px] text-gray-400 fw-bold pb-1 underline decoration-orange-200">Goal: ₹{totalPayout.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-slate-100 h-2 rounded overflow-hidden">
                        <div 
                            className="bg-orange-600 h-full transition-all duration-1000" 
                            style={{ width: `${totalPayout > 0 ? (candidatePaid / totalPayout) * 100 : 0}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            {/* Transaction History Table matching app theme */}
            <div className="tablebg rounded shadow-sm overflow-hidden border border-white">
                <div className="px-4 py-3 bg-white border-b flex justify-between items-center">
                    <h6 className="text-sm fw-bold text-gray-700 m-0 poppins">Transaction History</h6>
                    <span className="bg-slate-50 border px-2 py-0.5 rounded text-[10px] fw-bold text-gray-500 uppercase tracking-widest">Audit Log</span>
                </div>
                <div className="overflow-x-auto h-[40vh] overflow-y-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 bg-white shadow-sm z-10">
                            <tr className="text-[14px] text-gray-400 fw-bold border-b">
                                <th className="px-4 py-3">Billing Month</th>
                                <th className="px-4 py-3 text-center">Billed Amount</th>
                                <th className="px-4 py-3 text-center">Income Status</th>
                                <th className="px-4 py-3 text-center">Salary Payout</th>
                                <th className="px-4 py-3 text-center">Payout Status</th>
                                <th className="px-4 py-3 text-center">Net Margin</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-50">
                            {cycles.map((cycle) => (
                                <tr key={cycle.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col">
                                            <span className="text-sm fw-bold text-slate-700">{cycle.cycle_month}</span>
                                            <span className="text-[10px] text-slate-400">Due: {changeDateYear(cycle.due_date)}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <div className="flex flex-col items-center">
                                            <span className="text-sm fw-bold text-blue-600">₹{parseFloat(cycle.billing_amount).toLocaleString()}</span>
                                            <span className="text-[10px] text-slate-400">Paid: ₹{parseFloat(cycle.paid_amount || 0).toLocaleString()}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className={`px-2 py-1 rounded text-[10px] fw-bold uppercase border ${
                                            cycle.status === 'paid' ? 'bg-green-50 border-green-100 text-green-700' :
                                            cycle.status === 'partial' ? 'bg-purple-50 border-purple-100 text-purple-700' :
                                            'bg-yellow-50 border-yellow-100 text-yellow-700'
                                        }`}>
                                            {cycle.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <div className="flex flex-col items-center">
                                            <span className="text-sm fw-bold text-orange-600">₹{parseFloat(cycle.candidate_payout_amount || 0).toLocaleString()}</span>
                                            <span className="text-[10px] text-slate-400">Paid: ₹{parseFloat(cycle.candidate_paid_amount || 0).toLocaleString()}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className={`px-2 py-1 rounded text-[10px] fw-bold uppercase border ${
                                            cycle.payout_status === 'paid' ? 'bg-green-50 border-green-100 text-green-700' :
                                            cycle.payout_status === 'partial' ? 'bg-purple-50 border-purple-100 text-purple-700' :
                                            'bg-slate-50 border-slate-200 text-slate-400'
                                        }`}>
                                            {cycle.payout_status || 'Pending'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className="text-sm fw-bold text-green-700 bg-green-50 px-3 py-1 rounded border border-green-100">
                                            ₹{parseFloat(cycle.net_profit || 0).toLocaleString()}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default CandidateFinancialDetail
